// START GENAI
import {
  Component,
  h,
  State,
  Host,
  Prop,
  Event,
  EventEmitter,
  Element,
  Listen,
} from '@stencil/core';

interface CardBenefitMap {
  [cardType: string]: string[];
}

interface CardTypeLabels {
  [cardType: string]: string;
}

interface BenefitTypeLabels {
  [benefitType: string]: string;
}

@Component({
  tag: 'vs-adlibs',
  styleUrl: 'vs-adlibs.scss',
  shadow: false,
})
export class Adlibs {
  @Element() element: HTMLElement;
  @Prop() uniqueId!: string;
  @Prop() heading: string;
  @Prop() selectionPrefix: string;
  @Prop() connector: string;
  @Prop() cardTypes: string;
  @Prop() defaultCardType: string;
  @Prop() benefitTypes: string;
  @Prop() defaultBenefitType: string;

  // Helper method to format "low apr" as "Low APR" (preserving original capitalization)
  private formatLowApr(text: string): string {
    if (!text) return '';
    return text.replace(/apr/i, 'APR');
  }
  @Prop() resultsButtonText: string;
  @Prop() matchButtonText: string;
  @Prop() showSparkleIcon: boolean;
  @Prop() browseLinkText: string;
  @Prop() browseLinkUrl: string;
  @Prop() showArrowIcon: boolean;
  @Prop() backgroundImage: string;
  @Prop() mobileImage: string;
  @Prop() mobileImageAltText: string;
  @Prop() mediaType: 'image' | 'video' = 'image';
  @Prop() videoSrc: string;
  @Prop() mobileVideoSrc: string;
  @Prop() videoAlt: string;
  @Prop() autoplay: boolean = true;
  @Prop() muted: boolean = true;
  @Prop() theme: string;
  @Prop() ctaData: string;
  @Prop() seeAllCardsText: string;
  @Prop() seeAllCardsUrl: string;
  @Prop() seeAllBenefitsText: string;
  @Prop() seeAllBenefitsUrl: string;
  @Prop() cardTypesPanelTitle: string;
  @Prop() benefitTypesPanelTitle: string;
  @Event() themeChange: EventEmitter<string>;
  @State() cardType: string;
  @State() benefitType: string;
  @State() showCardTypePanel: boolean = false;
  @State() showBenefitTypePanel: boolean = false;
  @State() cardBenefitMap: CardBenefitMap = {};
  @State() cardTypeLabels: CardTypeLabels = {};
  @State() benefitTypeLabels: BenefitTypeLabels = {};
  @State() availableBenefitTypes: string[] = [];
  @State() availableCardTypes: string[] = [];

  private clickOutsideHandler: (event: Event) => void;
  private cardTypeTriggerElement: HTMLElement;
  private benefitTypeTriggerElement: HTMLElement;
  private focusableElements: HTMLElement[] = [];
  private currentFocusIndex: number = 0;

  toggleCardTypePanel = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    // Store reference to the triggering element
    this.cardTypeTriggerElement = event.target as HTMLElement;

    this.showCardTypePanel = !this.showCardTypePanel;
    if (this.showCardTypePanel) {
      this.showBenefitTypePanel = false;
      document.body.classList.add('no-scroll');
      // Setup focus management for the card type panel
      this.setupFocusManagement('card');
    } else {
      document.body.classList.remove('no-scroll');
    }
  };
  toggleBenefitTypePanel = (event: Event) => {
    // Only toggle if the card type is "credit card"
    if (this.isCreditCard()) {
      event.preventDefault();
      event.stopPropagation();

      // Store reference to the triggering element
      this.benefitTypeTriggerElement = event.target as HTMLElement;

      this.showBenefitTypePanel = !this.showBenefitTypePanel;
      if (this.showBenefitTypePanel) {
        document.body.classList.add('no-scroll');
        this.showCardTypePanel = false;
        // Setup focus management for the benefit type panel
        this.setupFocusManagement('benefit');
      } else {
        document.body.classList.remove('no-scroll');
      }
    }
  };

  toggleTheme = () => {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    this.themeChange.emit(this.theme);
  };

  selectCardType(type: string) {
    this.cardType = type;
    this.showCardTypePanel = false;

    // Update available benefit types based on selected card type
    this.updateAvailableBenefitTypes(type);

    // Update the CTA URLs based on the new card type
    this.updateCtaUrls();

    // Return focus to the triggering element after a short delay
    // This ensures the modal is fully closed before setting focus
    setTimeout(() => {
      if (this.cardTypeTriggerElement) {
        this.cardTypeTriggerElement.focus();
        document.body.classList.remove('no-scroll');
      }
    }, 50);
  }

  // Helper method to check if the current card type is "credit card"
  isCreditCard(): boolean {
    return this.cardType === 'credit card';
  }

  // Helper method to update CTA URLs based on card type and benefit type
  updateCtaUrls() {
    if (!this.ctaData) return;

    try {
      // Parse the current ctaData
      const ctaItems = JSON.parse(this.ctaData);
      if (!ctaItems || !ctaItems.length) return;

      // Make the URL relative to the current domain
      const currentDomain = window.location.origin;
      const pathPart = '/pay-with-visa/find-card';
      const baseUrl = `${currentDomain}${pathPart}`;

      // Define the benefit type mapping once
      const benefitTypeMap = {
        travel: 'travel',
        'travel benefits': 'travel',
        'cash back': 'cash_back',
        cashback: 'cash_back',
        'no annual fee': 'no_annual_fee',
        'low apr': 'low_apr',
        'balance transfer': 'balance_transfers',
        'balance transfers': 'balance_transfers',
        rewards: 'rewards',
      };

      // Format card type for URL (lowercase, trim spaces)
      const formattedCardType = this.cardType.toLowerCase().trim();

      // Prepare the path to append
      let appendPath = '';

      if (formattedCardType === 'credit card') {
        // For credit cards, append benefit type
        if (this.benefitType) {
          // Format benefit type for URL
          const formattedBenefitType = this.benefitType.toLowerCase().trim();
          const urlSlug =
            benefitTypeMap[formattedBenefitType] || formattedBenefitType.replace(/\s+/g, '_');

          appendPath = `/apply-credit-card/${urlSlug}`;
        } else {
          appendPath = '/apply-credit-card';
        }
      } else if (formattedCardType === 'debit card') {
        appendPath = '/apply-debit-card';
      } else if (
        formattedCardType === 'prepaid card' ||
        formattedCardType === 'pre-paid card' ||
        formattedCardType.includes('prepaid') ||
        formattedCardType.includes('pre-paid')
      ) {
        appendPath = '/get-prepaid-card';
      } else if (formattedCardType === 'gift card' || formattedCardType === 'giftcard') {
        appendPath = '/buy-gift-card';
      }

      // Update the results button URL if it exists
      if (ctaItems.length > 0) {
        // Keep all original properties but update the link
        ctaItems[0] = {
          ...ctaItems[0],
          link: `${baseUrl}${appendPath}`,
        };
      }

      // Apply the same URL update to the match button if it exists
      if (ctaItems.length > 1) {
        // Keep all original properties but update the link
        ctaItems[1] = {
          ...ctaItems[1],
          link: `${baseUrl}${appendPath}`,
        };
      }

      // Update the ctaData prop with the new URLs
      this.ctaData = JSON.stringify(ctaItems);
    } catch (error) {
      console.error('Error updating CTA URLs:', error);
    }
  }

  updateAvailableBenefitTypes(cardType: string) {
    // Check if we have a mapping for this card type
    if (this.cardBenefitMap && Object.keys(this.cardBenefitMap).length > 0) {
      // Use the original key from the data source
      if (this.cardBenefitMap[cardType]) {
        // Use the mapping directly
        this.availableBenefitTypes = this.cardBenefitMap[cardType];

        // If current benefit type is not in the available list, select the first one
        if (
          !this.availableBenefitTypes.includes(this.benefitType) &&
          this.availableBenefitTypes.length > 0
        ) {
          this.benefitType = this.availableBenefitTypes[0];
        }
      } else {
        // If no mapping exists, use all benefit types
        this.availableBenefitTypes = this.benefitTypes ? this.benefitTypes.split(',') : [];
      }
    } else {
      // If no card-benefit map is available, use all benefit types
      this.availableBenefitTypes = this.benefitTypes ? this.benefitTypes.split(',') : [];
    }
  }

  selectBenefitType(type: string) {
    this.benefitType = type;
    this.showBenefitTypePanel = false;

    // Update the CTA URLs based on the new benefit type
    this.updateCtaUrls();

    // Return focus to the triggering element after a short delay
    // This ensures the modal is fully closed before setting focus
    setTimeout(() => {
      if (this.benefitTypeTriggerElement) {
        this.benefitTypeTriggerElement.focus();
        document.body.classList.remove('no-scroll');
      }
    }, 50);
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    // Only handle keyboard events when a modal is actually open
    if (this.showCardTypePanel || this.showBenefitTypePanel) {
      // Check if the event target is within the modal panel
      const target = event.target as HTMLElement;
      const isWithinModal = target.closest('.vs-adlib-bottom-panel.show');

      if (isWithinModal) {
        switch (event.key) {
          case 'Escape':
            event.preventDefault();
            this.closeAllPanels();
            break;
          case 'Tab':
            event.preventDefault();
            this.handleTabNavigation(event.shiftKey);
            break;
          case 'ArrowDown':
            event.preventDefault();
            this.navigateOptions(1);
            break;
          case 'ArrowUp':
            event.preventDefault();
            this.navigateOptions(-1);
            break;
          case 'Enter':
          case ' ':
            // Only handle Enter/Space on option items, not on other elements like close button
            if (target.getAttribute('role') === 'option') {
              event.preventDefault();
              this.selectCurrentOption();
            }
            break;
        }
      } else if (event.key === 'Escape') {
        // Allow Escape to close modal even if focus is outside the modal
        event.preventDefault();
        this.closeAllPanels();
      }
    }
  }

  private closeAllPanels() {
    if (this.showCardTypePanel) {
      this.showCardTypePanel = false;
      document.body.classList.remove('no-scroll');
      setTimeout(() => {
        if (this.cardTypeTriggerElement) {
          this.cardTypeTriggerElement.focus();
        }
      }, 50);
    }
    if (this.showBenefitTypePanel) {
      this.showBenefitTypePanel = false;
      document.body.classList.remove('no-scroll');
      setTimeout(() => {
        if (this.benefitTypeTriggerElement) {
          this.benefitTypeTriggerElement.focus();
        }
      }, 50);
    }
  }

  private handleTabNavigation(shiftKey: boolean) {
    if (this.focusableElements.length === 0) return;

    if (shiftKey) {
      this.currentFocusIndex =
        this.currentFocusIndex <= 0
          ? this.focusableElements.length - 1
          : this.currentFocusIndex - 1;
    } else {
      this.currentFocusIndex =
        this.currentFocusIndex >= this.focusableElements.length - 1
          ? 0
          : this.currentFocusIndex + 1;
    }

    this.focusableElements[this.currentFocusIndex]?.focus();
  }

  private navigateOptions(direction: number) {
    if (this.focusableElements.length === 0) return;

    this.currentFocusIndex = Math.max(
      0,
      Math.min(this.focusableElements.length - 1, this.currentFocusIndex + direction)
    );

    this.focusableElements[this.currentFocusIndex]?.focus();
  }

  private selectCurrentOption() {
    const currentElement = this.focusableElements[this.currentFocusIndex];
    if (currentElement) {
      currentElement.click();
    }
  }

  private setupFocusManagement(panelType: 'card' | 'benefit') {
    setTimeout(() => {
      const panelId =
        panelType === 'card' ? `${this.uniqueId}-card-panel` : `${this.uniqueId}-benefit-panel`;

      const panel = document.getElementById(panelId);
      if (!panel) return;

      // Get all focusable elements in the panel
      this.focusableElements = Array.from(
        panel.querySelectorAll('li[role="option"], button, a[href]')
      ) as HTMLElement[];

      // Set focus to the first option in the list
      const firstOption = panel.querySelector('li[role="option"]') as HTMLElement;
      if (firstOption) {
        this.currentFocusIndex = 0;
        firstOption.focus();
      }
    }, 100);
  }

  private handleDropdownKeyDown = (event: KeyboardEvent, panelType: 'card' | 'benefit') => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // Only open the modal if it's not already open
      if (panelType === 'card' && !this.showCardTypePanel) {
        this.toggleCardTypePanel(event);
      } else if (panelType === 'benefit' && !this.showBenefitTypePanel) {
        this.toggleBenefitTypePanel(event);
      }
    }
  };

  componentWillLoad() {
    // Initialize state from props with fallbacks
    this.cardType = this.defaultCardType || '';
    this.benefitType = this.defaultBenefitType || '';

    // Ensure theme is set to a valid value (default to light if not specified)
    if (!this.theme || (this.theme !== 'light' && this.theme !== 'dark')) {
      this.theme = 'light';
    }

    // Try to parse the data-component attribute directly from the props
    if (this.element && this.element.getAttribute('data-component')) {
      try {
        const componentData = this.element.getAttribute('data-component');
        this.parseComponentData(componentData);
      } catch {
        // Error parsing component data from element
      }
    } else {
      // Wait for the DOM to be ready
      setTimeout(() => {
        this.loadComponentData();
      }, 500); // Increased timeout to ensure DOM is ready
    }

    // Initialize CTA URLs based on default card type and benefit type
    setTimeout(() => {
      this.updateCtaUrls();
    }, 600); // Ensure this runs after component data is loaded
  }

  parseComponentData(componentData: string) {
    if (!componentData) {
      this.fallbackToProps();
      return;
    }

    try {
      const parsedData = JSON.parse(componentData);

      // Parse video-related properties if available
      if (parsedData.mediaType) {
        this.mediaType = parsedData.mediaType;
      }

      // Map backgroundVideo to videoSrc
      if (parsedData.backgroundVideo) {
        this.videoSrc = parsedData.backgroundVideo;
      }

      // Map mobileBackgroundVideo to mobileVideoSrc
      if (parsedData.mobileBackgroundVideo) {
        this.mobileVideoSrc = parsedData.mobileBackgroundVideo;
      }

      if (parsedData.videoAlt) {
        this.videoAlt = parsedData.videoAlt;
      }

      if (parsedData.autoplay !== undefined) {
        this.autoplay = parsedData.autoplay;
      }

      if (parsedData.muted !== undefined) {
        this.muted = parsedData.muted;
      }

      // Get card-benefit map and labels from component data
      if (parsedData.cardBenefitMap && Object.keys(parsedData.cardBenefitMap).length > 0) {
        this.cardBenefitMap = parsedData.cardBenefitMap;

        // Get card type labels if available
        if (parsedData.cardTypeLabels && Object.keys(parsedData.cardTypeLabels).length > 0) {
          this.cardTypeLabels = parsedData.cardTypeLabels;
        }

        // Get benefit type labels if available
        if (parsedData.benefitTypeLabels && Object.keys(parsedData.benefitTypeLabels).length > 0) {
          this.benefitTypeLabels = parsedData.benefitTypeLabels;
        }

        // Extract available card types from the map keys and from props
        this.availableCardTypes = Object.keys(this.cardBenefitMap);

        // If the keys in the map don't match the format in props, use the props
        if (
          this.availableCardTypes.length === 0 ||
          (this.cardTypes && !this.availableCardTypes.some(type => this.cardTypes.includes(type)))
        ) {
          this.availableCardTypes = this.cardTypes
            ? this.cardTypes.split(',').map(c => c.trim())
            : [];
        }

        // Update available benefit types based on selected card type
        // Force a re-render by setting cardType again
        const currentCardType = this.cardType;
        this.cardType = '';
        setTimeout(() => {
          this.cardType = currentCardType;
          this.updateAvailableBenefitTypes(currentCardType);
        }, 0);
      } else {
        this.fallbackToProps();
      }
    } catch {
      this.fallbackToProps();
    }
  }

  fallbackToProps() {
    this.availableCardTypes = this.cardTypes ? this.cardTypes.split(',') : [];
    this.availableBenefitTypes = this.benefitTypes ? this.benefitTypes.split(',') : [];
  }

  loadComponentData() {
    // Try to parse component data from data-component attribute
    try {
      // Try to find the component element using different selectors
      let componentElement = document.querySelector(`vs-adlibs[uniqueid="${this.uniqueId}"]`);

      // If not found, try without the uniqueId
      if (!componentElement) {
        const allAdlibs = document.querySelectorAll('vs-adlibs');

        // Use the first one with a data-component attribute
        for (let i = 0; i < allAdlibs.length; i++) {
          if (allAdlibs[i].hasAttribute('data-component')) {
            componentElement = allAdlibs[i];
            break;
          }
        }
      }

      const componentData = componentElement?.getAttribute('data-component');

      if (componentData) {
        this.parseComponentData(componentData);
      } else {
        this.fallbackToProps();
      }
    } catch {
      // Fallback to props if there's an error
      this.availableCardTypes = this.cardTypes ? this.cardTypes.split(',') : [];
      this.availableBenefitTypes = this.benefitTypes ? this.benefitTypes.split(',') : [];
    }

    // Setup click outside handler
    this.clickOutsideHandler = (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target.closest('vs-adlibs')) {
        this.showCardTypePanel = false;
        this.showBenefitTypePanel = false;
        document.body.classList.remove('no-scroll');
      }
    };
  }

  componentDidLoad() {
    // Add click outside listener
    document.addEventListener('click', this.clickOutsideHandler);
  }

  disconnectedCallback() {
    // Remove click outside listener
    document.removeEventListener('click', this.clickOutsideHandler);
  }
  private renderBackground() {
    if (this.mediaType === 'video') {
      return (
        <div class="vs-adlib-video-container">
          <vsr-video
            src={this.videoSrc}
            mobile-src={this.mobileVideoSrc}
            video-type="video-loop"
            autoplay={this.autoplay}
            muted={this.muted}
            video-alt={this.videoAlt}
            playsinline={true}
            lazy-load={false}
          ></vsr-video>
        </div>
      );
    }
    return null;
  }

  render() {
    // Make sure theme is always a valid value (light or dark)
    const theme = this.theme === 'light' ? 'light' : 'dark';

    // Parse CTA data if available
    let ctaItems = [];
    try {
      if (this.ctaData) {
        ctaItems = JSON.parse(this.ctaData);
      }
    } catch {
      // Error parsing CTA data
    }

    // Get button text from CTA data if available, otherwise use props
    const resultsButtonText = ctaItems.length > 0 ? ctaItems[0].title : this.resultsButtonText;
    const matchButtonText = ctaItems.length > 1 ? ctaItems[1].title : this.matchButtonText;

    // Get button URLs from CTA data if available
    const resultsButtonUrl = ctaItems.length > 0 ? ctaItems[0].link : '';
    const matchButtonUrl = ctaItems.length > 1 ? ctaItems[1].link : '';

    // Get button targets from CTA data if available
    const resultsButtonTarget = ctaItems.length > 0 ? ctaItems[0].target : '_self';
    const matchButtonTarget = ctaItems.length > 1 ? ctaItems[1].target : '_self';

    // Set background style only if using image media type
    const style =
      this.mediaType === 'image' || this.mediaType === 'video'
        ? {
            ...(this.backgroundImage &&
              this.mobileImage && { '--adlib-bg': `url('${this.backgroundImage}')` }),
            ...(this.backgroundImage &&
              this.mobileImage && { '--adlib-bg-mobile': `url('${this.mobileImage}')` }),
          }
        : {};

    return (
      <Host>
        <div
          class={`vs-adlib-container ${!this.backgroundImage && this.mediaType !== 'video' ? `vs-adlib-theme-${theme}` : ''}`}
          style={style}
          role="region"
          aria-label="Card and benefit selection"
          id={this.uniqueId}
        >
          {this.mediaType === 'video' && this.renderBackground()}
          <div class="vs-adlib-container-wrapper">
            {this.heading && (
              <h2 class="vs-adlib-heading" id={`${this.uniqueId}-heading`}>
                {this.heading}
              </h2>
            )}

            <div class="vs-adlib-selection-container" aria-labelledby={`${this.uniqueId}-heading`}>
              <div class="vs-adlib-selection-row" data-connector={this.connector}>
                <span class="vs-adlib-selection-text">{this.selectionPrefix}</span>
                {this.cardType && (
                  <div class="vs-adlib-dropdown-container">
                    <button
                      class="vs-adlib-dropdown"
                      onClick={this.toggleCardTypePanel}
                      onKeyDown={e => this.handleDropdownKeyDown(e, 'card')}
                      aria-haspopup="listbox"
                      aria-expanded={this.showCardTypePanel ? 'true' : 'false'}
                      aria-controls={`${this.uniqueId}-card-panel`}
                      aria-label={`Select card type: ${this.cardType}`}
                      type="button"
                    >
                      <div class="vs-adlib-dropdown-content">
                        <span>{this.cardType}</span>
                      </div>
                      <div class="vs-adlib-dropdown-icon">
                        <span class="vs-adlib-arrow-down" aria-hidden="true"></span>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* Only show connector and benefit type for "credit card" */}
              {this.isCreditCard() && (
                <div class="vs-adlib-selection-row">
                  <span class="vs-adlib-selection-text vs-adlib-with-text vs-adlib-connector">
                    {this.connector}
                  </span>
                  {this.benefitType && (
                    <div class="vs-adlib-dropdown-container">
                      <button
                        class="vs-adlib-dropdown"
                        onClick={this.toggleBenefitTypePanel}
                        onKeyDown={e => this.handleDropdownKeyDown(e, 'benefit')}
                        aria-haspopup="listbox"
                        aria-expanded={this.showBenefitTypePanel ? 'true' : 'false'}
                        aria-controls={`${this.uniqueId}-benefit-panel`}
                        aria-label={`Select benefit type: ${this.benefitType}`}
                        type="button"
                      >
                        <div class="vs-adlib-dropdown-content">
                          <span>{this.formatLowApr(this.benefitType)}</span>
                        </div>
                        <div class="vs-adlib-dropdown-icon">
                          <span class="vs-adlib-arrow-down" aria-hidden="true"></span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div class="vs-adlib-button-container" role="group" aria-label="Action buttons">
              {resultsButtonText && resultsButtonUrl ? (
                <a
                  href={resultsButtonUrl}
                  target={resultsButtonTarget}
                  class="analytics-button vs-adlib-action-button vs-adlib-results-button"
                  aria-label={`${resultsButtonText} - opens ${resultsButtonTarget === '_blank' ? 'in new window' : 'in same window'}`}
                >
                  {resultsButtonText}
                </a>
              ) : resultsButtonText ? (
                <button
                  class="analytics-button vs-adlib-action-button vs-adlib-results-button"
                  aria-label={resultsButtonText}
                >
                  {resultsButtonText}
                </button>
              ) : null}

              {matchButtonText && matchButtonUrl ? (
                <a
                  href={matchButtonUrl}
                  target={matchButtonTarget}
                  class="analytics-button vs-adlib-action-button vs-adlib-match-button"
                  aria-label={`${matchButtonText} - opens ${matchButtonTarget === '_blank' ? 'in new window' : 'in same window'}`}
                >
                  <svg
                    class="vs-adlib-sparkle-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    role="img"
                  >
                    <path
                      d="M8 1.33l1.97 4 4.42.64-3.2 3.11.76 4.4L8 11.31l-3.95 2.07.76-4.4-3.2-3.11 4.42-.64L8 1.33z"
                      fill="currentColor"
                    />
                  </svg>
                  {matchButtonText}
                </a>
              ) : matchButtonText ? (
                <button
                  class="analytics-button vs-adlib-action-button vs-adlib-match-button"
                  aria-label={matchButtonText}
                >
                  <svg
                    class="vs-adlib-sparkle-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    role="img"
                  >
                    <path
                      d="M8 1.33l1.97 4 4.42.64-3.2 3.11.76 4.4L8 11.31l-3.95 2.07.76-4.4-3.2-3.11 4.42-.64L8 1.33z"
                      fill="currentColor"
                    />
                  </svg>
                  {matchButtonText}
                </button>
              ) : null}
            </div>

            {/* Only render the browse link if browseLinkText exists */}
            {this.browseLinkText && (
              <a
                href={this.browseLinkUrl}
                class="analytics-adlib-link vs-adlib-browse-link"
                aria-label={`${this.browseLinkText} - browse all options`}
              >
                {this.browseLinkText}
                {/* Only show arrow if browseLinkUrl exists, is not empty, and is a valid URL */}
                {this.browseLinkUrl &&
                  typeof this.browseLinkUrl === 'string' &&
                  this.browseLinkUrl.trim() !== '' &&
                  this.browseLinkUrl !== 'null' &&
                  this.browseLinkUrl !== 'undefined' && (
                    <span class="vs-adlib-arrow-right" aria-hidden="true"></span>
                  )}
              </a>
            )}
          </div>

          {/* Overlay backdrop */}
          {(this.showCardTypePanel || this.showBenefitTypePanel) && (
            <div
              class="vs-adlib-overlay"
              onClick={e => {
                e.preventDefault();
                this.closeAllPanels();
              }}
              aria-hidden="true"
            ></div>
          )}

          <div
            class={`vs-adlib-bottom-panel ${this.showCardTypePanel ? 'show' : ''}`}
            role="dialog"
            aria-labelledby={`${this.uniqueId}-card-panel-title`}
            aria-hidden={this.showCardTypePanel ? 'false' : 'true'}
            aria-modal={this.showCardTypePanel ? 'true' : 'false'}
            id={`${this.uniqueId}-card-panel`}
          >
            <button
              class="vs-adlib-close-btn"
              onClick={e => this.toggleCardTypePanel(e)}
              aria-label="Close card type selection panel"
              aria-controls={`${this.uniqueId}-card-panel`}
            >
              <span class="vs-adlib-close-icon" aria-hidden="true"></span>
            </button>
            <div class="vs-adlib-panel-content">
              <div class="vs-adlib-panel-left">
                <h3
                  class="vs-adlib-panel-name credit-panel-name"
                  id={`${this.uniqueId}-card-panel-title`}
                >
                  {this.cardTypesPanelTitle}
                </h3>
              </div>
              <div class="vs-adlib-panel-right">
                <div class="vs-adlib-panel-divider" aria-hidden="true"></div>
                <ul
                  class="vs-adlib-card-list"
                  role="listbox"
                  aria-label="Available card types"
                  aria-activedescendant={`card-option-${this.cardType.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {this.availableCardTypes.length > 0
                    ? this.availableCardTypes.map(cardType => {
                        // Use the original key from the data source
                        // Use label from cardTypeLabels which comes from the AEM data source
                        const displayText =
                          this.cardTypeLabels && this.cardTypeLabels[cardType]
                            ? this.cardTypeLabels[cardType]
                            : cardType; // Fallback to the key itself

                        const formattedType = cardType.trim();
                        const isSelected =
                          formattedType.toLowerCase() === this.cardType.toLowerCase();
                        const optionId = `card-option-${formattedType.replace(/\s+/g, '-').toLowerCase()}`;

                        return (
                          <li
                            class="vs-adlib-card-item"
                            onClick={() => this.selectCardType(formattedType)}
                            role="option"
                            aria-selected={isSelected ? 'true' : 'false'}
                            id={optionId}
                            tabindex="0"
                          >
                            {displayText}
                          </li>
                        );
                      })
                    : this.cardTypes &&
                      this.cardTypes.split(',').map(cardType => {
                        const formattedType = cardType.trim();
                        const isSelected =
                          formattedType.toLowerCase() === this.cardType.toLowerCase();
                        const optionId = `card-option-${formattedType.replace(/\s+/g, '-').toLowerCase()}`;

                        return (
                          <li
                            class="vs-adlib-card-item"
                            onClick={() => this.selectCardType(formattedType)}
                            role="option"
                            aria-selected={isSelected ? 'true' : 'false'}
                            id={optionId}
                            tabindex="0"
                          >
                            {formattedType.charAt(0).toUpperCase() + formattedType.slice(1)}
                          </li>
                        );
                      })}
                </ul>
              </div>
            </div>
            {this.seeAllCardsText && this.seeAllCardsUrl && (
              <div class="vs-adlib-panel-footer">
                <a
                  href={this.seeAllCardsUrl}
                  class="analytics-adlib-link vs-adlib-see-all-link credit-panel"
                  aria-label={`${this.seeAllCardsText} - opens in new window`}
                >
                  {this.seeAllCardsText}
                  <span class="vs-adlib-arrow-right" aria-hidden="true"></span>
                </a>
              </div>
            )}
          </div>

          {/* Only show benefit type panel for "credit card" */}
          {this.isCreditCard() && (
            <div
              class={`vs-adlib-bottom-panel ${this.showBenefitTypePanel ? 'show' : ''}`}
              role="dialog"
              aria-labelledby={`${this.uniqueId}-benefit-panel-title`}
              aria-hidden={this.showBenefitTypePanel ? 'false' : 'true'}
              aria-modal={this.showBenefitTypePanel ? 'true' : 'false'}
              id={`${this.uniqueId}-benefit-panel`}
            >
              <button
                class="vs-adlib-close-btn"
                onClick={e => this.toggleBenefitTypePanel(e)}
                aria-label="Close benefit type selection panel"
                aria-controls={`${this.uniqueId}-benefit-panel`}
              >
                <span class="vs-adlib-close-icon" aria-hidden="true"></span>
              </button>
              <div class="vs-adlib-panel-content">
                <div class="vs-adlib-panel-left">
                  <h3
                    class="vs-adlib-panel-name benefit-panel-name"
                    id={`${this.uniqueId}-benefit-panel-title`}
                  >
                    {this.benefitTypesPanelTitle}
                  </h3>
                </div>
                <div class="vs-adlib-panel-right">
                  <div class="vs-adlib-panel-divider" aria-hidden="true"></div>
                  <ul
                    class="vs-adlib-card-list"
                    role="listbox"
                    aria-label="Available benefit types"
                    aria-activedescendant={`benefit-option-${this.benefitType.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {this.availableBenefitTypes.length > 0
                      ? this.availableBenefitTypes.map(benefitType => {
                          // Use the original key from the data source
                          // Use label from benefitTypeLabels which comes from the AEM data source
                          const displayText =
                            this.benefitTypeLabels && this.benefitTypeLabels[benefitType]
                              ? this.benefitTypeLabels[benefitType]
                              : benefitType; // Fallback to the key itself

                          const formattedType = benefitType.trim();
                          const isSelected =
                            formattedType.toLowerCase() === this.benefitType.toLowerCase();
                          const optionId = `benefit-option-${formattedType.replace(/\s+/g, '-').toLowerCase()}`;

                          return (
                            <li
                              class="vs-adlib-card-item"
                              onClick={() => this.selectBenefitType(formattedType)}
                              role="option"
                              aria-selected={isSelected ? 'true' : 'false'}
                              id={optionId}
                              tabindex="0"
                            >
                              {this.formatLowApr(displayText)}
                            </li>
                          );
                        })
                      : this.benefitTypes &&
                        this.benefitTypes.split(',').map(benefitType => {
                          const formattedType = benefitType.trim();
                          const isSelected =
                            formattedType.toLowerCase() === this.benefitType.toLowerCase();
                          const optionId = `benefit-option-${formattedType.replace(/\s+/g, '-').toLowerCase()}`;

                          return (
                            <li
                              class="vs-adlib-card-item"
                              onClick={() => this.selectBenefitType(formattedType)}
                              role="option"
                              aria-selected={isSelected ? 'true' : 'false'}
                              id={optionId}
                              tabindex="0"
                            >
                              {this.formatLowApr(
                                formattedType.trim().charAt(0).toUpperCase() +
                                  formattedType.trim().slice(1)
                              )}
                            </li>
                          );
                        })}
                  </ul>
                </div>
              </div>
              {this.seeAllBenefitsText && this.seeAllBenefitsUrl && (
                <div class="vs-adlib-panel-footer">
                  <a
                    href={this.seeAllBenefitsUrl}
                    class="vs-adlib-see-all-link benefit-panel"
                    aria-label={`${this.seeAllBenefitsText} - opens in new window`}
                  >
                    {this.seeAllBenefitsText}
                    <span class="vs-adlib-arrow-right" aria-hidden="true"></span>
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </Host>
    );
  }
}
